import { StackIcon } from "@xola/icons";
import { almostZero } from "@xola/ui-kit";
import { isStoreCreditPurchaseItem } from "common";
import React, { useMemo } from "react";
import {
    Audit,
    LineItemType,
    Payment,
    PaymentLineItem,
    PaymentLineItemStatus,
    PaymentMethod,
    Purchase,
    PurchaseItem,
    RemoteCard,
} from "types";
import { useCollection } from "../../../../hooks/useCollection";
import { TimelineItem } from "../TimelineItem";
import PaymentTimelineItemContent from "./PaymentTimelineItemContent";

interface PaymentTimelineItemProps {
    purchase: Purchase;
    item: PurchaseItem;
    audit: Audit;
    lineItem: PaymentLineItem;
    showDate?: boolean;
}

export const PaymentTimelineItem = ({
    purchase,
    item,
    audit,
    lineItem,
    showDate = false,
}: PaymentTimelineItemProps) => {
    const [amount, payment, reason] = getAmount(lineItem);
    const { method } = payment;

    const isStoreCreditRefund = lineItem?.refundToStoreCredit;
    const isCreditCard = lineItem.payment?.method === PaymentMethod.CREDIT_CARD && !isStoreCreditRefund;
    const lineItemMetaCard = payment?.meta?.cardSummary ?? null;

    // Fetch saved cards only if its a cc line item and meta doesn't exist.
    const { data: savedCards } = useCollection<RemoteCard>(["purchases", purchase.id, "remoteCards"], null, {
        enabled: isCreditCard && !lineItemMetaCard,
    });

    const card = useMemo(() => {
        if (!isCreditCard) {
            return null;
        }

        if (lineItemMetaCard) {
            return lineItemMetaCard;
        }

        return savedCards?.find((savedCard) => savedCard.id === lineItem.payment?.card);
    }, [savedCards, lineItem, isCreditCard, lineItemMetaCard]);

    if (!PaymentTimelineItem.supportsAudit(purchase, item, audit, lineItem)) {
        return null;
    }

    return (
        <TimelineItem
            title={getTitle(lineItem.fundingSource, isStoreCreditPurchaseItem(item))}
            audit={lineItem}
            icon={<StackIcon />}
            color="success"
            showDate={showDate}
            traveler={lineItem.traveler}
            purchase={purchase}
        >
            <PaymentTimelineItemContent amount={amount} method={method} comment={reason} card={card} />
        </TimelineItem>
    );
};

PaymentTimelineItem.supportsAudit = (
    _purchase: Purchase,
    _item: PurchaseItem,
    _audit: Audit,
    lineItem: PaymentLineItem,
) => {
    // Checks if the lineItem from the audit is present in the purchase
    const isPresentInPurchase = _purchase.lineItems?.find((li) => li.id === lineItem?.id);
    return lineItem?.type === LineItemType.PAYMENT &&
        lineItem.status === PaymentLineItemStatus.COMPLETE &&
        isPresentInPurchase
        ? !almostZero(lineItem.amount)
        : false;
};

const getAmount = (lineItem: PaymentLineItem): [number, Payment, string] => {
    if (lineItem?.type === LineItemType.PAYMENT && lineItem.payment) {
        const lineItemComment =
            lineItem.payment.comment && lineItem.payment.comment !== ""
                ? lineItem.payment.comment
                : lineItem.name ?? "Initial charge";
        return [lineItem.amount * -1, lineItem.payment, lineItemComment];
    }

    return [0, { method: PaymentMethod.CHECK }, ""];
};

const getTitle = (fundingSource: any, isStoreCreditPurchaseItem: boolean) => {
    if (fundingSource) {
        if (isStoreCreditPurchaseItem) {
            return "Funds Added";
        }

        return "Transfer In";
    }

    return "Payment";
};
